import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../../components/ui';
import { CheckCircleIcon } from '../../../../components/ui/Icons';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('el-GR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MyProceduresDetailsTab = ({
  selectedRecord,
  fields,
  config,
  isPending,
  isAcknowledging,
  ackError,
  onAcknowledge,
}) => {
  const { theme } = useTheme();

  if (!selectedRecord) return null;

  if (selectedRecord.canView === false) {
    return (
      <div className="p-4 space-y-3 text-sm">
        <p className="text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
          This procedure is assigned to you but is not published yet. You will be able to view the
          current published revision and files once QMS publishes it.
        </p>
        <dl className="grid grid-cols-2 gap-3">
          <div>
            <dt className={theme.text.secondary}>Code</dt>
            <dd className={theme.text.primary}>{selectedRecord.code || '—'}</dd>
          </div>
          <div>
            <dt className={theme.text.secondary}>Title</dt>
            <dd className={theme.text.primary}>{selectedRecord.title || '—'}</dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ scrollbarWidth: 'thin', minHeight: 0, maxHeight: '100%' }}
    >
      <div
        className={cn(
          'mb-4 rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3',
          isPending
            ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
            : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
        )}
      >
        <div className="text-sm">
          {isPending ? (
            <p className="text-amber-800 dark:text-amber-200">
              <strong>Απαιτείται επιβεβαίωση.</strong> Δες τα στοιχεία και τα αρχεία, μετά πάτα
              «Επιβεβαίωση».
            </p>
          ) : (
            <p className="text-green-800 dark:text-green-200 flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 shrink-0" />
              <span>
                <strong>Επιβεβαιώθηκε</strong>
                {selectedRecord.viewedAt
                  ? ` · ${formatDateTime(selectedRecord.viewedAt)}`
                  : ''}
              </span>
            </p>
          )}
          {ackError && (
            <p className="text-red-600 dark:text-red-400 text-xs mt-1">{ackError}</p>
          )}
        </div>
        {isPending && onAcknowledge && (
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={onAcknowledge}
            disabled={isAcknowledging}
          >
            {isAcknowledging ? 'Επιβεβαίωση…' : 'Επιβεβαίωση'}
          </Button>
        )}
      </div>

      <p className={cn('text-xs uppercase tracking-wide mb-4', theme.text.secondary)}>
        Current published revision (read-only)
      </p>
      {config.formSections?.map((section, idx) => (
        <div key={idx} className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            {section.icon && <span className="text-base">{section.icon}</span>}
            <div className={cn('text-sm font-semibold', theme.text.primary)}>{section.title}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {section.fields.map((fieldName) => {
              const f = fields.find((ff) => ff.name === fieldName);
              if (!f || f.type === 'hidden') return null;

              let displayVal = selectedRecord[fieldName];
              const displayField = f.table?.displayField;
              if (displayField && selectedRecord[displayField] != null) {
                displayVal = selectedRecord[displayField];
              }

              if (f.type === 'date' && displayVal) {
                try {
                  displayVal = new Date(displayVal).toLocaleDateString();
                } catch {
                  /* keep raw */
                }
              }

              if (fieldName === 'viewStatus' && displayVal) {
                displayVal = displayVal === 'VIEWED' ? 'Viewed' : 'Pending';
              }

              return (
                <div key={fieldName}>
                  <div className={cn('text-xs mb-1', theme.text.secondary)}>{f.label}</div>
                  <div
                    className={cn(
                      'text-sm p-2 rounded',
                      theme.colors.background,
                      theme.text.primary
                    )}
                  >
                    {String(displayVal ?? '—')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {selectedRecord.currentRevisionDetail?.notes && (
        <div>
          <div className={cn('text-xs mb-1', theme.text.secondary)}>Revision notes</div>
          <div
            className={cn(
              'text-sm p-2 rounded whitespace-pre-wrap',
              theme.colors.background,
              theme.text.primary
            )}
          >
            {selectedRecord.currentRevisionDetail.notes}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProceduresDetailsTab;
